import React, { useCallback, useRef } from 'react';
import { mergeTests } from '../../../../ui-toolkit/test-utilities/mergeTests';
import { Combobox } from '@headlessui/react';
import { ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ComboBoxTWProps } from '../ComboBoxTW';
import { ComboboxFormCell } from './ComboboxFormCell';
import { SelectOption } from '../../interfaces';
import cx from 'classnames';

interface ComboboxSingleWrapperProps extends ComboBoxTWProps {
  setQuery: React.Dispatch<React.SetStateAction<string>>;
}

export const ComboboxSingleWrapper: React.FC<ComboboxSingleWrapperProps> = ({
  autoComplete,
  children,
  className,
  errorText,
  onChange,
  onBlur,
  disabled,
  value,
  hintText,
  labelText,
  isMenuPlacementTop,
  isNullable = false,
  isRequired,
  placeholder = '',
  setQuery,
  onClearSelection,
  ...rest
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const getDisplayValue = useCallback((item?: SelectOption | null) => item?.label ?? '', []);

  return (
    <Combobox
      as="div"
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      data-test-id={mergeTests('comboBox', rest['data-test-id'])}
      className={className}
      disabled={disabled}
    >
      {({ open }) => (
        <ComboboxFormCell errorText={errorText} hintText={hintText} labelText={labelText} isRequired={isRequired}>
          <div
            className={
              'relative h-10 w-full rounded-md border-0 bg-white py-1.5 pl-1.5 pr-8 shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-slate-800 ui-disabled:bg-gray-100'
            }
          >
            <Combobox.Input
              autoComplete={autoComplete}
              className={cx(
                'text-gray-900 placeholder:text-gray-400 focus:outline-none ui-disabled:text-gray-400 sm:text-sm sm:leading-6',
                {
                  'w-full': !isNullable,
                  'w-80 truncate': isNullable,
                }
              )}
              onChange={(event) => {
                event.stopPropagation();
                setQuery(event.target.value);
              }}
              onClick={() => {
                if (!open) {
                  buttonRef.current?.click();
                }
              }}
              displayValue={getDisplayValue}
              placeholder={placeholder}
            />
            {isNullable && value && (
              <button
                disabled={disabled}
                data-test-id="clear-selection-btn"
                className="absolute inset-y-0 right-0 flex items-center gap-2 rounded-r-md pr-8 focus:outline-none"
              >
                <XMarkIcon
                  data-test-id="clear-icon"
                  className="text-black-400 h-4 w-4 ui-disabled:text-gray-400"
                  aria-hidden="true"
                  onClick={onClearSelection}
                />
              </button>
            )}
            <Combobox.Button
              data-test-id="open-list-btn"
              className="absolute inset-y-0 right-0 flex items-center pr-2"
              ref={buttonRef}
            >
              <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </Combobox.Button>
          </div>
          {children}
        </ComboboxFormCell>
      )}
    </Combobox>
  );
};
